import React from 'react'
import { withStyles } from '@material-ui/core'
import styles from './styles'
import { headerDefaultTemplate } from 'src/components/editor/components/custom-plugins/simple-box/header-default-template'
import { headerHeroTemplate } from 'src/components/editor/components/custom-plugins/simple-box/header-hero-template'
import { headerPromoTemplate } from 'src/components/editor/components/custom-plugins/simple-box/header-promo-template'
import { useDispatch, useSelector } from 'react-redux'
import { editorEnum } from 'src/model/editor/editorEnum'
import { getCertificatePaymentSettingAction } from 'src/reducers/certificate/action'
import {
  useWorkspaceBranding,
  withWorkspaceStyle,
} from 'src/utils/workspace-logo'
import {
  isDefaultHeaderImage,
  primaryHeaderBackground,
} from 'src/utils/default-header-background'

/**
 * The landing page header preview. `previewTemplate` is a preset being
 * previewed: its sections, font and color are shown over the saved page.
 */
const Header = ({ classes, pageUrl, previewTemplate, inPortal }) => {
  const dispatch = useDispatch()
  const course = useSelector(state => state.course.course)
  const { collection } = useSelector(state => state.collection)
  const courseSettings = useSelector(state => state.course.landingPageSettings)
  const collectionSettings = useSelector(
    state => state.collection.landingPageSettings,
  )
  const collectionGeneralSettings = useSelector(
    state => state.collection.collectionSettings,
  )
  const certificate = useSelector(state => state.certificate?.certificate)
  const paymentSetting = useSelector(state => state.certificate?.paymentSetting)

  // The certification settings tab fetches paymentSetting on demand, but the
  // landing-page editor route doesn't go through there — fetch it ourselves
  // so the indicator can render on first load.
  React.useEffect(() => {
    if (pageUrl === 'mini-course') return
    if (!collection?.id) return
    if (paymentSetting !== null && paymentSetting !== undefined) return
    dispatch(getCertificatePaymentSettingAction(collection.id))
  }, [collection?.id, pageUrl, paymentSetting, dispatch])

  const certificatePayment =
    pageUrl === 'mini-course'
      ? null
      : {
          isConfigured:
            !!certificate?.isActive &&
            !!paymentSetting?.isPaymentConfigured &&
            (paymentSetting?.amount ?? 0) > 0,
          amount: paymentSetting?.amount,
          currency: paymentSetting?.currency,
          priceType: paymentSetting?.priceType,
          priceMetadata: paymentSetting?.priceMetadata,
        }

  const workspaceBranding = useWorkspaceBranding()
  const workspaceLogo = workspaceBranding?.logo || null
  // Pages on the default color and font show the workspace's.
  // Courses too: a course on the default color and font shows the workspace's.
  const savedLandingPageData = withWorkspaceStyle(
    pageUrl === 'mini-course' ? courseSettings : collectionSettings,
    workspaceBranding,
  )
  const previewData =
    previewTemplate && savedLandingPageData
      ? {
          ...savedLandingPageData,
          font: previewTemplate.font,
          primaryColor: previewTemplate.primaryColor,
          sections: previewTemplate.value,
        }
      : savedLandingPageData
  // A page without a logo shows the workspace logo, as learners see it on a
  // direct link. In the portal the header already shows it, so not twice.
  const landingPageData =
    previewData && !previewData.logo && workspaceLogo && !inPortal
      ? { ...previewData, logo: workspaceLogo }
      : previewData

  // DÜZELTME: null kontrolü eklendi
  const landingPageSection = landingPageData?.sections?.find(
    x => x.type === editorEnum.headerType,
  )

  // DÜZELTME: landingPageSection kontrolü eklendi
  const getContentData = () => {
    if (!landingPageSection?.content) {
      return { image: '', alignment: 'center' }
    }
    return landingPageSection.content
  }

  // Process the image URL to add base URL if needed
  const processImageUrl = url => {
    if (!url) return ''
    return url.startsWith('https://')
      ? url
      : `https://cdn.minicoursegenerator.com/${url}`
  }

  const { image, alignment } = getContentData()
  const processedImage = processImageUrl(image)

  // DEBUG: Image değerlerini kontrol et
  React.useEffect(() => {
    console.log('=== IMAGE DEBUG ===')
    console.log('landingPageData:', landingPageData)
    console.log('landingPageSection:', landingPageSection)
    console.log('image:', image)
    console.log('processedImage:', processedImage)
    console.log('==================')
  }, [landingPageData, landingPageSection, image, processedImage])

  // Define styles based on alignment
  const getAlignmentStyles = () => ({
    textAlign: alignment,
    alignItems:
      alignment === 'center'
        ? 'center'
        : alignment === 'right'
        ? 'flex-end'
        : 'flex-start',
  })

  // Get background style
  const getBackgroundStyle = () => {
    // DÜZELTME: Erken kontrol eklendi
    if (!landingPageSection?.content) return null

    const type = landingPageSection.content.type

    return {
      ...(type === 2 && {
        backgroundColor:
          landingPageSection.content.backgroundColor || '#FFFFFF',
      }),
      // DÜZELTME: processedImage kontrolü eklendi
      ...(type !== 2 &&
        processedImage && {
          // The Default preset's image is drawn in the page's primary color.
          background: isDefaultHeaderImage(processedImage)
            ? primaryHeaderBackground(landingPageData?.primaryColor)
            : `linear-gradient(0deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.3) 100%), 
                  url(${processedImage}) 50% center / cover no-repeat lightgray`,
        }),
      ...(type === 3 &&
        {
          //marginBottom: '76px',
        }),
    }
  }

  // Select template based on header type
  const getHeaderTemplate = (
    landingPageData,
    course,
    collection,
    pageUrl,
    collectionSettings,
    collectionGeneralSettings,
  ) => {
    if (landingPageSection?.content?.type === undefined) {
      return null
    }

    const headerType = landingPageSection.content.type
    switch (headerType) {
      case 2: // HERO
        return headerHeroTemplate(
          landingPageData,
          course,
          collection,
          pageUrl,
          collectionSettings,
          collectionGeneralSettings,
          certificatePayment,
        )

      case 3: // PROMO
        return headerPromoTemplate(
          landingPageData,
          course,
          collection,
          pageUrl,
          collectionSettings,
          collectionGeneralSettings,
          certificatePayment,
        )
      case 1:
        return headerDefaultTemplate(
          landingPageData,
          course,
          collection,
          pageUrl,
          collectionSettings,
          collectionGeneralSettings,
        )
      default:
        return headerDefaultTemplate(
          landingPageData,
          course,
          collection,
          pageUrl,
          collectionSettings,
          collectionGeneralSettings,
        )
    }
  }

  return (
    <div className="editor-header-preview">
      <div className="editor-header-container" style={getBackgroundStyle()}>
        <div
          className="editor-header-content"
          style={landingPageData ? getAlignmentStyles() : null}
          dangerouslySetInnerHTML={{
            __html: getHeaderTemplate(
              landingPageData,
              course,
              collection,
              pageUrl,
              collectionSettings,
              collectionGeneralSettings,
            ),
          }}
        />
      </div>
    </div>
  )
}

export default withStyles(styles)(Header)
